import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Translate from './Translate';
import logoImg from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { i18n } = useTranslation();

  const currentLang = i18n.language || 'mr';

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'About', path: '/about' },
    { 
      name: 'Departments', 
      type: 'dropdown',
      items: [
        { name: 'Administration', path: '/departments/administration' },
        { name: 'Account', path: '/departments/accounts' },
        { name: 'Labour Time Office', path: '/departments/labour-time' },
        { name: 'Engineering Department', path: '/departments/engineering' },
        { name: 'Cane Development / Farming', path: '/departments/cane-development' },
        { name: 'Purchase Department', path: '/departments/purchase' },
        { name: 'Godown Department', path: '/departments/godown' },
        { name: 'Security Department', path: '/departments/security' },
      ]
    },
    { name: 'Products', path: '/products' },
    { name: 'Our Strengths', path: '/our-strengths' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'News', path: '/news' },
    { name: 'Downloads', path: '/downloads' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const isParentActive = (link) => {
    if (link.type === 'dropdown') {
      return link.items.some(item => location.pathname === item.path);
    }
    return location.pathname === link.path;
  };

  const toggleDropdown = (name) => {
    if (activeDropdown === name) setActiveDropdown(null);
    else setActiveDropdown(name);
  };

  return (
    <>
      {/* ── Pre-header Bar (Hidden on Mobile/Tablet) ── */}
      <div className="hidden lg:block bg-forest text-white/70 text-xs font-body relative z-[60]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex justify-between items-center h-9">
          <div className="flex items-center space-x-6">
            <a href="tel:02346226240" className="flex items-center space-x-1.5 hover:text-gold-light transition-colors">
              <Phone size={11} />
              <span>(02346) 226240</span>
            </a>
            <a href="mailto:sangramcaneagroltd@gmail.com" className="flex items-center space-x-1.5 hover:text-gold-light transition-colors">
              <Mail size={11} />
              <span>sangramcaneagroltd@gmail.com</span>
            </a>
          </div>
          <div className="flex items-center space-x-1.5 text-white/40">
            <span>Sangram Cane Agro Limited</span>
            <span className="mx-2">·</span>
            <span>Palus, Dist. Sangli</span>
          </div>
        </div>
      </div>

      {/* ── Main Navbar ── */}
      <nav className={`sticky top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.06)] py-2' 
          : 'bg-white py-3'
      }`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src={logoImg} 
                alt="Sangram Sugarcane Logo" 
                className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-10 sm:h-11' : 'h-12 sm:h-14'}`}
              />
            </Link>

            {/* ── Desktop Menu (Large screens >= lg) ── */}
            <div className="hidden lg:flex items-center lg:gap-x-1.5 ml-2 xl:ml-6 flex-nowrap">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div 
                    key={link.name} 
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(link.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button
                      className={`flex items-center space-x-0.5 px-1.5 py-1.5 font-body font-semibold text-[14px] xl:text-[15px] tracking-wide transition-all duration-300 rounded-lg relative cursor-pointer
                        ${(activeDropdown === link.name || isParentActive(link)) ? 'text-emerald' : 'text-charcoal/80 hover:text-emerald'}`}
                    >
                      <span><Translate>{link.name}</Translate></span>
                      <ChevronDown size={12} className={`transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                      {isParentActive(link) && (
                        <motion.div
                          layoutId="nav-indicator"
                          className="absolute bottom-0 left-1.5 right-1.5 h-[2px] bg-gold rounded-full"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </button>
                    <AnimatePresence>
                      {activeDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 12 }}
                          transition={{ duration: 0.25, ease: "easeOut" }}
                          className="absolute left-1/2 -translate-x-1/2 mt-2 w-[480px] bg-white/95 backdrop-blur-md rounded-2xl shadow-premium-xl p-5 border border-gray-100/80 overflow-hidden z-50 grid grid-cols-2 gap-x-4 gap-y-1"
                        >
                          {/* Gold-emerald top accent line */}
                          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-gold to-emerald"></div>
                          {link.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              className={`flex flex-col justify-center px-4 py-2.5 rounded-xl transition-all duration-200 group/item
                                ${isActive(item.path) 
                                  ? 'text-emerald bg-forest-50 font-bold' 
                                  : 'text-charcoal/80 hover:bg-ivory hover:text-emerald'
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[12px] xl:text-[13px] font-heading font-semibold tracking-wide">
                                  <Translate>{item.name}</Translate>
                                </span>
                                <span className="text-emerald/0 group-hover/item:text-emerald group-hover/item:translate-x-1 transition-all duration-200 text-xs">
                                  →
                                </span>
                              </div>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-1.5 py-1.5 font-body font-semibold text-[14px] xl:text-[15px] tracking-wide transition-all duration-300 rounded-lg relative whitespace-nowrap
                      ${isActive(link.path) 
                        ? 'text-emerald' 
                        : 'text-charcoal/80 hover:text-emerald'
                      }`}
                  >
                    <Translate>{link.name}</Translate>
                    {isActive(link.path) && (
                      <motion.div
                        layoutId="nav-indicator"
                        className="absolute bottom-0 left-1.5 right-1.5 h-[2px] bg-gold rounded-full"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                )
              ))}

              {/* Language Switcher Desktop */}
              <div className="flex items-center space-x-1 ml-2 border-l border-gray-200 pl-2 flex-shrink-0">
                <button 
                  onClick={() => changeLanguage('mr')}
                  className={`px-2 py-0.5 text-[9px] font-heading font-extrabold uppercase rounded-md transition-all cursor-pointer ${
                    currentLang === 'mr' 
                      ? 'bg-emerald text-white shadow-emerald' 
                      : 'text-slate hover:bg-ivory hover:text-emerald'
                  }`}
                >
                  मराठी
                </button>
                <button 
                  onClick={() => changeLanguage('en')}
                  className={`px-2 py-0.5 text-[9px] font-heading font-extrabold uppercase rounded-md transition-all cursor-pointer ${
                    currentLang === 'en' 
                      ? 'bg-emerald text-white shadow-emerald' 
                      : 'text-slate hover:bg-ivory hover:text-emerald'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>

            {/* ── Mobile/Tablet Menu Hamburger Button (Screens < lg) ── */}
            <div className="lg:hidden flex items-center space-x-3">
              <button 
                onClick={() => setIsOpen(true)} 
                className="p-2.5 rounded-2xl bg-gray-50 border border-gray-200 hover:bg-ivory transition-colors text-charcoal shadow-sm cursor-pointer"
                aria-label="Open Mobile Menu"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile / Tablet Navigation Drawer Overlay (Redesigned matching reference image) ── */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Blur */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] lg:hidden"
            />

            {/* Slide-in Drawer Container */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed inset-y-0 right-0 w-full max-w-sm sm:max-w-md bg-white z-[101] lg:hidden shadow-2xl flex flex-col justify-between font-poppins"
            >
              {/* 1. Dark Top Header Bar (Matching Image 1) */}
              <div className="bg-[#181818] px-6 py-5 flex items-center justify-between shadow-md">
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-white p-1 flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
                    <img 
                      src={logoImg} 
                      alt="Sangram Sugarcane Logo" 
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-heading font-extrabold text-white text-base tracking-wider leading-tight">
                      SANGRAM
                    </span>
                    <span className="font-heading font-bold text-emerald text-[9px] tracking-widest uppercase">
                      CANE AGRO LTD.
                    </span>
                  </div>
                </Link>

                <button 
                  onClick={() => setIsOpen(false)} 
                  className="p-2 rounded-xl text-white hover:bg-white/10 hover:text-emerald transition-colors cursor-pointer"
                  aria-label="Close Menu"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Scrollable Main Menu Body */}
              <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col justify-between">
                
                {/* 2. Language Selector Box (Matching Image 1) */}
                <div className="bg-gray-50 border border-gray-200/60 rounded-2xl p-3.5 mb-6 flex items-center justify-between shadow-sm">
                  <span className="text-[11px] font-heading font-extrabold uppercase tracking-widest text-gray-400">
                    LANGUAGE
                  </span>
                  
                  <div className="bg-gray-200/80 p-1 rounded-full flex items-center gap-1">
                    <button
                      onClick={() => changeLanguage('mr')}
                      className={`transition-all duration-200 cursor-pointer ${
                        currentLang === 'mr'
                          ? 'bg-[#181818] text-white font-heading font-extrabold px-4 py-1.5 rounded-full text-xs shadow-sm'
                          : 'px-3 py-1.5 text-xs font-heading font-bold text-gray-600 hover:text-charcoal'
                      }`}
                    >
                      मराठी
                    </button>
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`transition-all duration-200 cursor-pointer ${
                        currentLang === 'en'
                          ? 'bg-[#181818] text-white font-heading font-extrabold px-4 py-1.5 rounded-full text-xs shadow-sm'
                          : 'px-3 py-1.5 text-xs font-heading font-bold text-gray-600 hover:text-charcoal'
                      }`}
                    >
                      EN
                    </button>
                  </div>
                </div>

                {/* 3. Navigation Links List (Matching Image 1 typography) */}
                <div className="space-y-1">
                  {navLinks.map((link) => (
                    link.type === 'dropdown' ? (
                      <div key={link.name} className="border-b border-gray-100/80 py-1">
                        <button 
                          onClick={() => toggleDropdown(link.name)}
                          className={`w-full flex justify-between items-center py-3 text-lg font-heading font-bold transition-colors cursor-pointer
                            ${(activeDropdown === link.name || isParentActive(link)) ? 'text-emerald' : 'text-charcoal hover:text-emerald'}`}
                        >
                          <span><Translate>{link.name}</Translate></span>
                          <ChevronDown size={20} className={`text-slate transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180 text-emerald' : ''}`} />
                        </button>

                        {/* Accordion list of 10 departments */}
                        <AnimatePresence>
                          {activeDropdown === link.name && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25 }}
                              className="overflow-hidden"
                            >
                              <div className="bg-gray-50 rounded-2xl my-2 p-2 space-y-1 border border-gray-100">
                                {link.items.map((item) => (
                                  <Link
                                    key={item.name}
                                    to={item.path}
                                    onClick={() => setIsOpen(false)}
                                    className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-heading font-bold transition-colors
                                      ${isActive(item.path) 
                                        ? 'text-emerald bg-white shadow-sm' 
                                        : 'text-gray-600 hover:text-emerald hover:bg-white/50'}`}
                                  >
                                    <span><Translate>{item.name}</Translate></span>
                                    <span className="text-xs opacity-40">→</span>
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between py-3.5 text-lg font-heading font-bold border-b border-gray-100/80 transition-colors
                          ${isActive(link.path) ? 'text-emerald' : 'text-charcoal hover:text-emerald'}`}
                      >
                        <span><Translate>{link.name}</Translate></span>
                      </Link>
                    )
                  ))}
                </div>

              </div>

              {/* 4. Bottom Drawer Footer CTA (Matching Image 1) */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 rounded-t-3xl shadow-inner flex flex-col items-center space-y-3">
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-3.5 bg-emerald hover:bg-forest text-white text-center rounded-2xl font-heading font-extrabold text-sm tracking-wide shadow-lg shadow-emerald/20 transition-all duration-300 block cursor-pointer"
                >
                  <Translate>Contact Us Now</Translate>
                </Link>

                <a 
                  href="tel:02346226240" 
                  className="flex items-center space-x-2 text-slate text-xs font-heading font-bold hover:text-emerald transition-colors"
                >
                  <Phone size={14} className="text-emerald" />
                  <span>Contact: (02346) 226240</span>
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
